import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { acceptAdminInvite, getAdminInviteByToken } from '../services/adminInvitationsApi';
import { trustDevice } from '../../utils/deviceTrust';
import { ADMIN_PATHS, adminPath } from '../../constants/adminPaths';
import { adminLocationHref } from '../../config/adminHost';
import { useControlPanelBranding } from '../../context/control-panel/ControlPanelBrandingContext';
import './AdminInviteAcceptPage.css';

export default function AdminInviteAcceptPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { name: controlPanelName } = useControlPanelBranding();
  const token = searchParams.get('token') || '';

  const [invite, setInvite] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [pwScore, setPwScore] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing invite token');
      return;
    }
    const load = async () => {
      try {
        const data = await getAdminInviteByToken(token);
        setInvite(data);
        setEmail(data?.email || '');
      } catch (e) {
        setError(e?.message || 'Invalid invite');
      }
    };
    load();
  }, [token]);

  // Simple password strength estimator: 0-4
  useEffect(() => {
    const value = password || '';
    let score = 0;
    if (value.length >= 6) score += 1;
    if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score += 1;
    if (/\d/.test(value)) score += 1;
    if (/[^A-Za-z0-9]/.test(value) || value.length >= 12) score += 1;
    setPwScore(score);
  }, [password]);

  const pwLabel = useMemo(() => ['Too weak', 'Weak', 'Fair', 'Good', 'Strong'][pwScore], [pwScore]);
  const pwClass = useMemo(() => ['weak', 'weak', 'fair', 'good', 'strong'][pwScore], [pwScore]);

  const handleAccept = async () => {
    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!token) {
      setError('Missing invite token.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await acceptAdminInvite({ token, firstName, lastName, password });

      // Give backend a moment to propagate profile/claims
      await new Promise((r) => setTimeout(r, 800));

      // Ensure they never get stuck on the access-code gate after accepting an invite.
      // (The access-code gate is only for reaching the admin login screen.)
      trustDevice();
      window.location.replace(adminLocationHref(ADMIN_PATHS.SUPPORT_LOGIN));
    } catch (e) {
      setError(e?.message || 'Failed to accept invite');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (loading) return;
    navigate(adminPath(ADMIN_PATHS.LOGIN));
  };

  return (
    <div className="admin-invite-accept-page">
      <div className="admin-invite-accept-card">
        <div className="admin-invite-accept-header">
          <div className="admin-invite-accept-logo">S</div>
          <div>
            <div className="admin-invite-accept-title">Accept Admin Panel Invitation</div>
            <div className="admin-invite-accept-subtitle">Join {controlPanelName}</div>
          </div>
        </div>

        <div className="admin-invite-accept-body">
          {invite && (
            <div className="admin-invite-accept-banner">
              You were invited as a <strong style={{ textTransform: 'capitalize' }}>{invite.role}</strong>.{' '}
              <span className="admin-invite-accept-muted">({invite.email})</span>
            </div>
          )}

          <div className="admin-invite-accept-field">
            <label className="admin-invite-accept-label">Email</label>
            <input
              className="admin-invite-accept-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!!invite?.email}
            />
          </div>

          <div className="admin-invite-accept-field">
            <label className="admin-invite-accept-label">
              Password
              <span className="admin-invite-accept-info-dot" title="How to reach Strong">
                i
                <span className="admin-invite-accept-tooltip">
                  Use <b>12+ characters</b> with a mix of <b>upper/lowercase</b>, <b>numbers</b>, and <b>symbols</b>.
                </span>
              </span>
            </label>
            <div className="admin-invite-accept-input-wrap">
              <input
                className="admin-invite-accept-input"
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="admin-invite-accept-toggle-eye"
                onClick={() => setShowPw((s) => !s)}
              >
                {showPw ? 'Hide' : 'Show'}
              </button>
            </div>
            <div className="admin-invite-accept-pw-meter">
              <div className="admin-invite-accept-pw-bars">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className={`admin-invite-accept-pw-bar ${pwScore > i ? `active ${pwClass}` : ''}`}
                  />
                ))}
              </div>
              <div className="admin-invite-accept-pw-label">{pwLabel}</div>
            </div>
          </div>

          <div className="admin-invite-accept-field">
            <label className="admin-invite-accept-label">Re-enter Password</label>
            <div className="admin-invite-accept-input-wrap">
              <input
                className="admin-invite-accept-input"
                type={showPw2 ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={
                  password && confirmPassword && password !== confirmPassword
                    ? { borderColor: '#dc2626' }
                    : undefined
                }
              />
              <button
                type="button"
                className="admin-invite-accept-toggle-eye"
                onClick={() => setShowPw2((s) => !s)}
              >
                {showPw2 ? 'Hide' : 'Show'}
              </button>
            </div>
            {password && confirmPassword && password !== confirmPassword && (
              <div className="admin-invite-accept-error">Passwords do not match</div>
            )}
          </div>

          <div className="admin-invite-accept-row">
            <div className="admin-invite-accept-field">
              <label className="admin-invite-accept-label">First Name</label>
              <input
                className="admin-invite-accept-input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="admin-invite-accept-field">
              <label className="admin-invite-accept-label">Last Name</label>
              <input
                className="admin-invite-accept-input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          {error && <div className="admin-invite-accept-error">{error}</div>}
        </div>

        <div className="admin-invite-accept-footer">
          <div className="admin-invite-accept-actions">
            <button className="admin-invite-accept-btn admin-invite-accept-btn-outline" onClick={handleCancel}>
              Back to Login
            </button>
            <button
              className="admin-invite-accept-btn admin-invite-accept-btn-primary"
              onClick={handleAccept}
              disabled={loading || !token || !email || !password || password !== confirmPassword}
            >
              {loading && <span className="admin-invite-accept-spinner" />}
              {loading ? 'Joining...' : 'Join Admin Panel'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
